import "./Transcription.css";
import { useState } from "react";
import Layout from "../components/Layout";
import axios from "axios";
import config from "../config";
import FileDropComponent from "../components/FileDropComponent";

function Transcription() {
  const [audioFile, setAudioFile] = useState(null);
  const [speakerSection, setSpeakerSection] = useState(false);
  const [transcriptFilename, setTranscriptFilename] = useState("");
  const [speakersSummary, setSpeakersSummary] = useState({});
  const [speakerNames, setSpeakerNames] = useState({});
  const [fileUploading, setFileUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [statusMessage, setStatusMessage] = useState(
    "Upload an audio file to begin transcription."
  );
  const [errorMessage, setErrorMessage] = useState("");

  const resetState = () => {
    setAudioFile(null);
    setSpeakerSection(false);
    setTranscriptFilename("");
    setSpeakersSummary({});
    setSpeakerNames({});
    setFileUploading(false);
    setIsLoading(false);
    setPrompt("");
    setErrorMessage("");
    setStatusMessage("Upload an audio file to begin transcription.");
  };

  const onAudioFileUpload = (extractedColumns, uploadedFile) => {
    setAudioFile(uploadedFile);
    setFileUploading(false);
    setErrorMessage("");
    setStatusMessage(
      uploadedFile
        ? `Ready to transcribe: ${uploadedFile.name}`
        : "Upload an audio file to begin transcription."
    );
  };

  const handleSpeakerNameChange = (speakerKey, newName) => {
    setSpeakerNames((prevNames) => ({
      ...prevNames,
      [speakerKey]: newName,
    }));
  };

  const transcribeAudio = async () => {
    if (!audioFile) {
      setErrorMessage("Please upload an audio file first.");
      setStatusMessage("Waiting for audio file upload.");
      return;
    }

    setIsLoading(true);
    setFileUploading(true);
    setErrorMessage("");
    setStatusMessage("Uploading audio and starting transcription...");
    const formData = new FormData();
    const allowedTypes = [
      "audio/mpeg",
      "audio/mp4",
      "audio/wav",
      "audio/x-m4a",
      "audio/aac",
      "audio/ogg",
      "video/mp4",
      "audio/mp3",
    ];
    if (allowedTypes.includes(audioFile.type)) {
      formData.append("audio_input", audioFile);
      formData.append("prompt", prompt);
    } else {
      setErrorMessage(
        "Only MP3, MP4, WAV, M4A, AAC, and OGG files are allowed at the moment."
      );
      setStatusMessage("File validation failed.");
      setIsLoading(false);
      setFileUploading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${config.api.baseUrl}/transcription/mp3`,
        formData
      );

      const responseData = response?.data || {};
      const summaries = responseData.summaries || {};
      const speakerKeys = Object.keys(summaries);
      const speakerCount =
        responseData.speaker_count !== undefined
          ? responseData.speaker_count
          : speakerKeys.length;

      if (!responseData.success || !responseData.message) {
        throw new Error(
          responseData.error ||
            "Transcription failed. Please try again with another file."
        );
      }

      if (!speakerCount || speakerKeys.length === 0) {
        setSpeakerSection(false);
        setSpeakersSummary({});
        setSpeakerNames({});
        setTranscriptFilename("");
        setStatusMessage(
          "Transcription finished, but no speakers were detected. Try a clearer file or check audio quality."
        );
        setErrorMessage(
          responseData.error ||
            "No speakers were identified in this file. Please upload another recording."
        );
        return;
      }

      const initialSpeakerNames = {};
      speakerKeys.forEach((speakerKey, index) => {
        initialSpeakerNames[speakerKey] = `Speaker ${index + 1}`;
      });

      setSpeakersSummary(summaries);
      setSpeakerNames(initialSpeakerNames);
      setTranscriptFilename(responseData.message);
      setSpeakerSection(true);
      setStatusMessage(
        responseData.job_status_message ||
          `Transcription complete. ${speakerKeys.length} speakers detected.`
      );
    } catch (error) {
      console.error("There was an error uploading the data:", error);
      const backendMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "There was an error transcribing your file.";
      setSpeakerSection(false);
      setSpeakersSummary({});
      setSpeakerNames({});
      setTranscriptFilename("");
      setErrorMessage(backendMessage);
      setStatusMessage("Transcription failed.");
    } finally {
      setFileUploading(false);
      setIsLoading(false);
    }
  };

  const updateSpeakers = async () => {
    setErrorMessage("");
    setStatusMessage("Applying speaker names and preparing download...");
    try {
      const response = await axios.post(
        `${config.api.baseUrl}/transcription/add_speakers_and_send`,
        {
          filename: transcriptFilename,
          speaker_names: speakerNames,
        },
        {
          responseType: "blob",
        }
      );

      // Creating a URL for the blob
      const fileURL = window.URL.createObjectURL(new Blob([response.data]));

      // Creating a temporary link element
      const fileLink = document.createElement("a");

      fileLink.href = fileURL;
      fileLink.setAttribute("download", transcriptFilename); // or another filename you want
      document.body.appendChild(fileLink);

      fileLink.click(); // Simulate click on the link to start download

      document.body.removeChild(fileLink); // Clean-up
      setStatusMessage("Transcript is ready and has started downloading.");
    } catch (error) {
      console.error("There was an error uploading the data:", error);
      let backendMessage = "There was an error finalizing the transcript.";
      const responseBlob = error?.response?.data;

      if (responseBlob instanceof Blob) {
        try {
          const blobText = await responseBlob.text();
          const parsed = JSON.parse(blobText);
          backendMessage = parsed?.error || parsed?.message || backendMessage;
        } catch (_blobParseError) {
          backendMessage = error?.message || backendMessage;
        }
      } else {
        backendMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          backendMessage;
      }

      setErrorMessage(backendMessage);
      setStatusMessage("Unable to finalize transcript.");
    } finally {
      return 1;
    }
  };

  return (
    <>
      <Layout>
        <div className="script-main-div">
          <div className="script-main-content">
            <div className="transcription-status-wrap">
              <div className="transcription-status">{statusMessage}</div>
              {errorMessage && (
                <div className="transcription-status transcription-status-error">
                  {errorMessage}
                </div>
              )}
            </div>
            {!speakerSection ? (
              <div className="script-holder">
                <div className="toyota-file-drop">
                  <FileDropComponent
                    onFileUpload={onAudioFileUpload}
                    className="file-area"
                  />
                  <div className="script-second-content">
                    <button
                      className={`transcript-btn ${
                        fileUploading ? "script-disabled" : ""
                      }`}
                      onClick={transcribeAudio}
                      disabled={fileUploading}
                    >
                      Transcribe Audio
                    </button>
                  </div>
                </div>
                <div className="script-third-content">
                  <div className="script-description-section">
                    {!isLoading ? (
                      <>
                        <h2>Audio Transcription Service</h2>
                        <p>
                          This tool is used to convert an audio file into a
                          transcript. File processing may take multiple minutes
                          depending on file size. Once the file is processed,
                          edit the generated speaker names based on the first 3
                          spoken lines from a given speaker.
                        </p>
                      </>
                    ) : (
                      <div className="transcription-loading-state">
                        <div className="spinner"></div>
                        <p>
                          Transcribing audio and identifying speakers. This can
                          take a few minutes.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="script-holder-2">
                {speakerSection && (
                  <div className="script-speaker-section">
                    <div className="script-speaker-header">
                      <h2>Name Speakers</h2>
                      <p>
                        Review each preview and set the final names before
                        downloading your transcript.
                      </p>
                    </div>
                    <div className="script-speaker-grid">
                      {Object.entries(speakersSummary).map(
                        ([speakerKey, lines], index) => (
                          <div key={index} className="speaker-summary">
                            <label
                              htmlFor={`speaker-name-${index}`}
                              className="speaker-input-label"
                            >
                              Speaker {index + 1} Name
                            </label>
                            <input
                              id={`speaker-name-${index}`}
                              type="text"
                              placeholder={`Speaker ${index + 1}`}
                              value={speakerNames[speakerKey] || ""}
                              onChange={(e) =>
                                handleSpeakerNameChange(
                                  speakerKey,
                                  e.target.value
                                )
                              }
                            />
                            <p>{lines}</p>
                          </div>
                        )
                      )}
                    </div>
                    <div className="transcript-btn-section">
                      <button
                        className="transcript-btn right-side-btn"
                        onClick={updateSpeakers}
                      >
                        Submit Speakers
                      </button>
                      <button className="reset-btn" onClick={resetState}>
                        Reset and Upload New File
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Layout>
    </>
  );
}

export default Transcription;
